import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'
import { getCurrentOnboardingVariant } from 'helpers/getCurrentOnboardingVariant'
import { sanitizeEventProperty } from 'helpers/sanitizeEventProperty'
import type {
  IEventLogger,
  IEventLogParams,
  TQuestionPageParams,
} from 'models/events.model'
import { EventLoggerInstanceName, OPTIMIZED_VARIANT_ID } from 'root-constants'
import {
  buildPrepaidCardBlockedProps,
  buildPurchaseProps,
  Events,
  ScreenName,
  type IPurchaseCommonProps,
  type TCtaBtnClickedParams,
  type TPaymentMethodPageShownParams,
  type TPrepaidCardBlockedParams,
  type TPricesViewedParams,
} from 'services/eventLogger.helpers'

export { Events, ScreenName } from 'services/eventLogger.helpers'

class EventLoggerService {
  private loggers?: Map<EventLoggerInstanceName, IEventLogger>
  private eventsQueue: IEventLogParams[] = []
  private commonEventProps: Record<string, unknown> = {}

  init(
    loggers: IEventLogger[],
    _utmTags: Record<string, string>,
    commonEventProps: Record<string, unknown>,
  ): void {
    const entriesArr = loggers.map(
      (logger) =>
        [logger.name, logger] as [EventLoggerInstanceName, IEventLogger],
    )
    this.loggers = new Map(entriesArr)
    this.commonEventProps = commonEventProps
  }

  setCommonEventProps(patch: Record<string, unknown>): void {
    this.commonEventProps = { ...this.commonEventProps, ...patch }
  }

  getCommonEventProps(): Record<string, unknown> {
    return this.commonEventProps
  }

  addLoggers(loggers: IEventLogger[]): void {
    loggers.forEach((logger) => {
      this.loggers?.set(logger.name, logger)
    })
  }

  hasLogger(name: EventLoggerInstanceName): boolean {
    return !!this.loggers?.has(name)
  }

  logSessionStarted = ({
    cohort,
    abVariant,
    abSegmentName,
  }: {
    cohort: string
    abVariant?: string | string[]
    abSegmentName?: string | string[]
  }): void => {
    const event = Events.SESSION_STARTED
    const commonProps = {
      ...this.commonEventProps,
      attribution: {
        ...(this.commonEventProps?.attribution ?? {}),
      },
    }

    const eventProperty = {
      device_type: getMobileOperatingSystem(),
      ab_variant:
        abVariant || getCurrentOnboardingVariant(OPTIMIZED_VARIANT_ID),
      ...(abSegmentName && { ab_segment_name: abSegmentName }),
      viewport_width: window.screen.width,
      viewport_height: window.screen.height,
      viewport_size: `${window.screen.width}x${window.screen.height}`,
      cohort,
      ...commonProps,
    }

    this.logEvent({ event, eventProperty })
    this.executeAndClearEventsQueue()
  }

  logFbcSegmentActivated({
    abVariant,
    abSegmentName,
  }: {
    abVariant: string | string[]
    abSegmentName: string | string[]
  }): void {
    const event = Events.SEGMENT_ACTIVATED
    const eventProperty = {
      ab_variant: abVariant,
      ab_segment_name: abSegmentName,
      ...this.commonEventProps,
    }

    this.logEventOrPushToQueue({
      event,
      eventProperty,
      includedLoggersNames: [EventLoggerInstanceName.RUDDERSTACK],
    })
  }

  logQuestion({
    question,
    answers,
    pageNumber,
    customProperties = {},
    event = Events.ONBOARDING_PAGE_COMPLETED,
  }: TQuestionPageParams): void {
    const eventProperty = {
      page_number: pageNumber,
      question,
      ...(answers && {
        answer: !Array.isArray(answers) ? answers.split(',') : answers,
      }),
      ...this.commonEventProps,
      ...customProperties,
    }
    this.logEventOrPushToQueue({ event, eventProperty })
  }

  updateUserPropertiesByLogger({
    loggersNames,
    options,
  }: {
    loggersNames: EventLoggerInstanceName[]
    options: Record<string, any>
  }): void {
    loggersNames.forEach((loggerName) => {
      const logger = this.loggers?.get(loggerName)
      logger?.updateUserProperties && logger.updateUserProperties(options)
    })
  }

  logEmailPageShown(): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_PAGE_SHOW,
      eventProperty: { ...this.commonEventProps },
      isPageEvent: true,
    })
  }

  logEmailExtraScreenShown(): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_EXTRA_SCREEN_SHOW,
      eventProperty: { ...this.commonEventProps },
      isPageEvent: true,
    })
  }

  logEmailPageCompleted({
    email,
    emailConsent,
  }: {
    email: string
    emailConsent?: boolean
  }): void {
    const eventProperty = {
      email,
      ...(emailConsent !== undefined && { email_consent: emailConsent }),
      ...this.commonEventProps,
    }

    this.updateUserPropertiesByLogger({
      loggersNames: [
        EventLoggerInstanceName.AMPLITUDE,
        EventLoggerInstanceName.RUDDERSTACK,
      ],
      options: { email },
    })
    this.logEventOrPushToQueue({
      event: Events.EMAIL_PAGE_COMPLETED,
      eventProperty,
    })
  }

  logEmailPageError(eventProperty: { error: string }): void {
    this.logEventOrPushToQueue({
      event: Events.EMAIL_PAGE_ERROR,
      eventProperty: { eventProperty, ...this.commonEventProps },
    })
  }

  logSalePageShown({
    email,
    screenName,
  }: {
    email: string
    screenName?: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.SALE_SCREEN_SHOW,
      eventProperty: {
        screen_name: screenName || ScreenName.SALES,
        email,
        ...this.commonEventProps,
      },
    })
  }

  logPricesViewed({
    method,
    screenName,
    email,
    scrollLvl,
  }: TPricesViewedParams): void {
    this.logEventOrPushToQueue({
      event: Events.VIEW_PRICE,
      eventProperty: {
        method,
        screen_name: screenName,
        email,
        ...(scrollLvl && { scroll_lvl: scrollLvl }),
        ...this.commonEventProps,
      },
    })
  }

  logPurchaseShown({
    email,
    screenName,
    productName,
  }: {
    email: string
    screenName?: string
    productName?: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_SHOW,
      eventProperty: {
        screen_name: screenName || ScreenName.SALES,
        email,
        ...(productName && { product_name: productName }),
        ...this.commonEventProps,
      },
    })
  }

  logPaymentMethodPageShown({
    email,
    screenName,
    method,
    btnLocation,
    btnName,
    scrollLvl,
  }: TPaymentMethodPageShownParams): void {
    this.logEventOrPushToQueue({
      event: Events.PAYMENT_METHOD_PAGE_SHOW,
      eventProperty: {
        screen_name: screenName || ScreenName.SALES,
        email,
        ...(method && { method }),
        ...(btnLocation && { btn_location: btnLocation }),
        ...(btnName && { btn_name: btnName }),
        ...(scrollLvl && { scroll_lvl: scrollLvl }),
        ...this.commonEventProps,
      },
    })
  }

  logCtaBtnClicked(props: TCtaBtnClickedParams): void {
    this.logEventOrPushToQueue({
      event: Events.CTA_BTN_CLICKED,
      eventProperty: {
        screen_name: props.screenName,
        email: props.email,
        ...(props.btnLocation && { btn_location: props.btnLocation }),
        ...(props.btnName && { btn_name: props.btnName }),
        ...(props.scrollLvl && { scroll_lvl: props.scrollLvl }),
        ...this.commonEventProps,
      },
    })
  }

  logPaymentMethodPageClose({
    email,
    screenName,
    productName,
  }: {
    email: string
    screenName?: string
    productName?: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.SUBS_PURCHASE_CLOSE,
      eventProperty: {
        screen_name: screenName || ScreenName.SALES,
        email,
        ...(productName && { product_name: productName }),
        ...this.commonEventProps,
      },
    })
  }

  logPurchaseStarted(props: IPurchaseCommonProps): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_STARTED,
      eventProperty: {
        ...buildPurchaseProps(props),
        ...this.commonEventProps,
      },
    })
  }

  logPurchaseCompleted(
    props: IPurchaseCommonProps & {
      purchaseId: string
      subscriptionId?: string
      transactionId?: string
      productPrice?: string
      isUpsell?: boolean
      customProperties?: Record<string, unknown>
    },
  ): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_COMPLETED,
      eventProperty: {
        ...buildPurchaseProps(props),
        purchase_id: props.purchaseId,
        ...(props.subscriptionId && { subscription_id: props.subscriptionId }),
        ...(props.transactionId && { transaction_id: props.transactionId }),
        ...(props.productPrice && { product_price: props.productPrice }),
        ...(!props.isUpsell &&
          props.priceDetails && {
            intro_price:
              props.priceDetails.trialPrice ?? props.priceDetails.price,
          }),
        ...this.commonEventProps,
        ...(props.customProperties ?? {}),
      },
    })
  }

  logPurchaseFailed(
    props: IPurchaseCommonProps & {
      error: {
        type?: string
        code?: string
        description?: string
        declineCode?: string
      }
      isRetry?: boolean
    },
  ): void {
    this.logEventOrPushToQueue({
      event: Events.PURCHASE_FAILED,
      eventProperty: {
        ...buildPurchaseProps(props),
        error_type: props.error.type || 'unknown',
        error_code: props.error.code || 'unknown',
        error_description: props.error.description || 'unknown',
        error_decline_code: props.error.declineCode || 'unknown',
        ...(props.isRetry !== undefined && { is_retry: props.isRetry }),
        ...this.commonEventProps,
      },
    })
  }

  logPrepaidCardBlocked(props: TPrepaidCardBlockedParams): void {
    this.logEventOrPushToQueue({
      event: Events.PREPAID_CARD_BLOCKED,
      eventProperty: {
        ...buildPrepaidCardBlockedProps(props),
        ...this.commonEventProps,
      },
    })
  }

  logCreateAccountShown(): void {
    this.logEventOrPushToQueue({
      event: Events.CREATE_ACCOUNT_PAGE,
      eventProperty: { ...this.commonEventProps },
    })
  }

  logLoginMethodSelected({ method }: { method: string }): void {
    this.logEventOrPushToQueue({
      event: Events.LOGIN_METHOD_SELECTED,
      eventProperty: {
        method,
        ...this.commonEventProps,
      },
    })
  }

  logAccountCreated({
    method,
    email,
  }: {
    method: string | null
    email: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.ACCOUNT_CREATED,
      eventProperty: {
        method,
        email,
        ...this.commonEventProps,
      },
    })
  }

  logAccountCreationFailed({ error }: { error: string }): void {
    this.logEventOrPushToQueue({
      event: Events.ACCOUNT_CREATION_FAILED,
      eventProperty: {
        error,
        ...this.commonEventProps,
      },
    })
  }

  logSupportButtonClicked({
    pageNumber,
    destination,
  }: {
    pageNumber: number | null
    destination: string
  }): void {
    this.logEventOrPushToQueue({
      event: Events.SUPPORT_BTN_CLICK,
      eventProperty: {
        page_number: pageNumber,
        destination,
        ...this.commonEventProps,
      },
    })
  }

  logScreenPresented({
    screenName,
    customProperties = {},
  }: {
    screenName: string
    customProperties?: Record<string, unknown>
  }): void {
    this.logEventOrPushToQueue({
      event: Events.SCREEN_PRESENTED,
      eventProperty: {
        screen_name: screenName,
        ...customProperties,
        ...this.commonEventProps,
      },
    })
  }

  logCertificateClaimed(customProperties: Record<string, unknown>): void {
    this.logEventOrPushToQueue({
      event: Events.IQ_CERTIFICATE_CLAIMED,
      eventProperty: {
        ...customProperties,
        ...this.commonEventProps,
      },
    })
  }

  logEventOrPushToQueue(value: IEventLogParams): void {
    if (this.loggers?.size) {
      this.logEvent(value)
    } else {
      this.eventsQueue.push(value)
    }
  }

  private executeAndClearEventsQueue() {
    if (this.eventsQueue.length) {
      this.eventsQueue.forEach((value) => this.logEvent(value))
      this.eventsQueue = []
    }
  }

  private logEvent(value: IEventLogParams): void {
    const sanitized: IEventLogParams = {
      ...value,
      eventProperty: sanitizeEventProperty(value.eventProperty),
    }
    this.loggers?.forEach((logger) => {
      if (sanitized.includedLoggersNames?.length) {
        sanitized.includedLoggersNames.includes(logger.name) &&
          logger.log(sanitized)
        return
      }

      logger.log(sanitized)
    })
  }
}

export const eventLogger = new EventLoggerService()
